import { assign, createMachine } from 'xstate';

import { sendParent } from 'xstate/lib/actions';
import { NavigationSections } from '../constants';
import { AssignApplyResult } from './actions/sharedNetworkActions';
import { identify, page, track } from './actions/segment';
import { EcommMachineEvents } from './types/ecommMachine.types';
import {
  NewApplicationEvents,
  NewApplicationMachineContext,
  NewApplicationMachineEvents,
  NewApplicationMachineGuards,
  NewApplicationMachineStates,
  NewApplicationStates,
} from './types/newApplicationMachine.types';
import apply from '../networkRequests/apply/apply';
import { MakeStateCallingQueryOrder } from './refresh-order';
import { sharedActions } from './actions/genericActions';

export default createMachine<NewApplicationMachineContext, NewApplicationMachineEvents, NewApplicationMachineStates>(
  {
    id: 'NewApplication',
    initial: NewApplicationStates.AccommodationAddress,
    context: {} as NewApplicationMachineContext, // Nothing you put here matters, the init block is the data in the `src: NewApplicationMachine` line in ecomm-machine
    on: {
      [NewApplicationEvents.TRACK]: {
        actions: (context, event: any) => track(event?.data?.eventName, event?.data?.payload)(context, event),
      },
      [NewApplicationEvents.PAGE]: {
        actions: (context, event: any) => page(event?.data?.pageName, event?.data?.payload)(context, event),
      },
      [NewApplicationEvents.IDENTIFY]: {
        actions: (context, event: any) => identify(event?.data?.payload),
      },
    },
    entry: 'log',
    states: {
      [NewApplicationStates.AccommodationAddress]: {
        on: {
          [NewApplicationEvents.NEXT]: {
            actions: 'assignAccommodationAddress',
            target: NewApplicationStates.ApplyLoading,
          },
          [NewApplicationEvents.BACK]: {
            actions: sendParent((context, event) => ({
              type: EcommMachineEvents.BACK,
            })),
            type: 'final',
          },
          [NewApplicationEvents.VIEW_PREQUALIFICATION_NOTICE]: {
            actions: 'assignAccommodationAddress',
            target: 'PrequalificationNotice',
          },
        },
        meta: {
          navigationSection: NavigationSections.CHECK_ELIGIBILITY,
          canGoBackViaHeader: true,
        },
      },
      [NewApplicationStates.PrequalificationNotice]: {
        on: {
          [NewApplicationEvents.NEXT]: {
            target: NewApplicationStates.AccommodationAddress,
          },
        },
      },
      [NewApplicationStates.ApplyLoading]: {
        initial: 'callingApply',
        states: {
          callingApply: {
            invoke: {
              src: apply,
              // wait to handle apply result until after query order
              onDone: { target: 'callingQueryOrder', actions: AssignApplyResult },
              onError: '#NewApplication.AccommodationAddressErrors', // TODO: Are you sure its not vvv?
              // onError: { actions: PropagateNetworkErrors() },
            },
          },
          callingQueryOrder: MakeStateCallingQueryOrder('select'),
          select: {
            always: [
              { cond: 'LeaseDecline', target: '#NewApplication.LeaseDeclined' },
              {
                cond: 'LTOIneligibleAutoVertical',
                target: '#NewApplication.LTOIneligible',
                actions: [
                  track('Non Leaseable Vertical in State', { errorType: 'Ineligible' }),
                  assign({
                    autoVerticalError: true,
                  }),
                ],
              },
              {
                cond: 'LTOIneligible',
                target: '#NewApplication.LTOIneligible',
                actions: [track('Non Leaseable State', { errorType: 'Ineligible' })],
              },
              {
                cond: 'ApplyIneligible',
                target: '#NewApplication.Ineligible',
                actions: [track('Non-Leaseable State', { errorType: 'Ineligible' })],
              },
              { cond: 'GeneralApplyErrors', target: '#NewApplication.GeneralError' },
              { cond: 'SoftDecline', target: '#NewApplication.AccommodationAddressErrors' },
              { cond: 'AppApproved', target: 'approved' },
              { target: '#NewApplication.AccommodationAddress', actions: [AssignApplyResult, 'log'] },
            ],
          },
          // temporary state to handle the sendParent. This is because transitions in a always block must have a target or they'll loop
          approved: {
            type: 'final',
            entry: sendParent((context) => ({ type: EcommMachineEvents.EXISTING_APPLICATION, data: { ...context } })),
          },
        },
        exit: (context, _event) => track('Application Submitted', { hasItems: !!context?.orderDetails?.items?.length }),
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
        },
      },
      [NewApplicationStates.LoanDeclined]: {},
      [NewApplicationStates.LeaseDeclined]: {},
      [NewApplicationStates.LTOIneligible]: {},
      [NewApplicationStates.Ineligible]: {},
      [NewApplicationStates.GeneralError]: {
        // type: 'final',
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
        },
      },
      [NewApplicationStates.AccommodationAddressErrors]: {
        entry: (context, _event) => track('Application Error', { errorType: 'softDecline', decisionCode: context?.application?.lease?.decisionCode }),
        on: {
          [NewApplicationEvents.BACK]: {
            target: NewApplicationStates.AccommodationAddress,
          },
        },
        meta: {
          navigationSection: NavigationSections.CHECK_ELIGIBILITY,
        },
      },
    },
    schema: {
      context: {} as any,
      events: {} as NewApplicationMachineEvents,
      guards: {} as NewApplicationMachineGuards,
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      assignAccommodationAddress: assign({
        accommodationAddress: (_context, event: any) => event.data,
        billingAddress: (_context, event: any) => event.data,
      }),
      ...sharedActions,
    },
    guards: {
      LeaseDecline: (context, event) => {
        if (context.applyErrors?.length) {
          return context.applyErrors.some((err) => err.__typename === 'HardDecline');
        }
        return false;
      },
      LTOIneligible: (context, event) => {
        if (context.applyErrors?.length) {
          return context.applyErrors.some((err) => err.__typename === 'LeaseRestricted');
        }
        return false;
      },
      ApplyIneligible: (context, event) => {
        if (context.applyErrors?.length) {
          return context.applyErrors.some((err) => err.__typename === 'Ineligible');
        }
        return false;
      },
      LTOIneligibleAutoVertical: (context, event) => {
        if (context.applyErrors?.length) {
          return context.applyErrors.some((err) => err.__typename === 'LeaseRestricted' && err.extensions?.reason === 'AutoVertical');
        }
        return false;
      },
      SoftDecline: (context, event) => {
        if (context.applyErrors?.length) {
          return context.applyErrors.some((err) => err.__typename === 'SoftDecline');
        }
        return false;
      },
      GeneralApplyErrors: (context, event) => {
        if (context.applyErrors?.length) {
          // This handles __typename === 'FatalError' || 'Ineligible'
          const otherErrors = context.applyErrors.some((err) => {
            // I think this check could be simplified. Guard conditions are checked in order,
            // so we already know that SoftDecline, LeaseRestricted, and HardDecline are not
            // in the array.  So all we should need to check for is CartValidation here
            return (
              err.__typename !== 'SoftDecline' &&
              err.__typename !== 'LeaseRestricted' &&
              err.__typename !== 'HardDecline' &&
              err.__typename !== 'CartValidation'
            );
          });
          return !!event?.data?.applyErrors?.length && otherErrors;
        }
        return false;
      },
      AppApproved: (context, event) => {
        // If the error is cart validation, still want to show approval page...
        if (context.applyErrors?.length) {
          return context.applyErrors.some((err) => err.__typename === 'CartValidation');
        } else if (!context.applyErrors || context.applyErrors.length === 0) {
          return (
            context.application?.lease?.status === 'preApproved' ||
            (context.application?.lease?.isOtb && context.application?.lease?.status === 'approved') || // TODO: need to requery order
            context.application?.loan?.status === 'approved'
          );
        }
        return false;
      },
    },
  },
);
